import { Flex, Heading, IconAddLine, IconButton, Text } from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import RichTextEditor from "react-rte";
import { formatToReadableTime } from "../../../utils/utils";
import { sortByLastName } from "../../Assessment/Rater/utils";
import { Card } from "../../Card";
import { MULTI, Selector } from "../../Selectors/Selector";
import { TermSelect } from "../../TermManagement/Terms/TermSelect";
import { CourseReviewContext } from "../CourseReview";

const FacultyReflections = () => {
  const { reflections, draftData, setDraftData } =
    useContext(CourseReviewContext);
  const location = useLocation();
  const { relevantTerms } = location.state;
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [filteredReflections, setFilteredReflections] = useState([]);

  const sortedAuthors = Array.from(
    new Set(reflections.map((r) => r.metadata.author))
  )
    .map((author) => ({ label: author, id: author, value: author }))
    .sort(sortByLastName);

  useEffect(() => {
    let filteredReflections = reflections.filter((ref) =>
      relevantTerms.find((t) => t.id === ref.term_id)
    );
    if (selectedTerms.length > 0) {
      filteredReflections = filteredReflections.filter((ref) =>
        selectedTerms.find((term) => term === ref.term_id)
      );
    }
    if (selectedAuthors.length > 0) {
      filteredReflections = filteredReflections.filter((ref) =>
        selectedAuthors.find((author) => author === ref.metadata.author)
      );
    }
    setFilteredReflections(filteredReflections);
  }, [reflections, selectedTerms, selectedAuthors, relevantTerms]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        height: "100%",
      }}
    >
      <Heading level="h3" as="h2" border="bottom">
        {t("courseReview.facultyReflections")}
      </Heading>
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem" }}>
        <div style={{ width: "50%" }}>
          <TermSelect
            id="faculty-reflection-term-select"
            label={t("terms")}
            selectHandler={(terms) => setSelectedTerms(terms)}
            selectedTerms={selectedTerms}
            size="small"
            filteredTermIds={relevantTerms.map((t) => t.id)}
          />
        </div>
        <div style={{ width: "50%" }}>
          <Selector
            id="faculty-reflection-author-select"
            type={MULTI}
            label={t("courseReview.authors")}
            selectHandler={(authors) => setSelectedAuthors(authors)}
            selectedOptions={selectedAuthors}
            size="small"
            options={sortedAuthors}
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: "0.75rem",
          margin: "-0.75rem",
          overflowY: "auto",
        }}
      >
        {filteredReflections.length === 0 ? (
          <Text>{t("reflections.noReflections")}</Text>
        ) : (
          filteredReflections.map((ref, key) => (
            <Card
              key={key}
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                height: "auto",
              }}
            >
              <Flex
                alignItems="start"
                gap="small"
                margin="0 0 small 0"
                wrap="wrap"
              >
                <Flex.Item shouldShrink shouldGrow>
                  <Text as="p" fontStyle="italic" size="small">
                    {ref.metadata.author}
                    {" - "}
                    {formatToReadableTime(new Date(ref.timestamp))}
                  </Text>
                </Flex.Item>
                <Flex.Item align="end">
                  <IconButton
                    screenReaderLabel={t("courseReview.addReflection")}
                    disabled={draftData.selectedReflections.find(
                      (r) => r.id.split("=")[0] === ref.id.split("=")[0]
                    )}
                    onClick={() => {
                      setDraftData((prev) => ({
                        ...prev,
                        selectedReflections: [ref, ...prev.selectedReflections],
                      }));
                    }}
                    renderIcon={IconAddLine}
                    color="success"
                    size="small"
                  />
                </Flex.Item>
              </Flex>
              {ref.from_template ? (
                <Flex wrap="wrap" gap="medium">
                  {ref.sections.map((section) => (
                    <Flex.Item key={section.id} width="100%">
                      <Flex wrap="wrap" gap="small">
                        <Flex.Item width="100%">
                          <Text weight="bold">{section.header_question}</Text>
                        </Flex.Item>
                        <Flex.Item>
                          <div
                            style={{
                              borderLeft: "2px solid #03893D",
                              paddingLeft: "0.75rem",
                            }}
                          >
                            <RichTextEditor
                              className="readOnlyClass"
                              value={RichTextEditor.createValueFromString(
                                section.content,
                                "html"
                              )}
                              readOnly
                            />
                          </div>
                        </Flex.Item>
                      </Flex>
                    </Flex.Item>
                  ))}
                </Flex>
              ) : (
                <RichTextEditor
                  className="readOnlyClass"
                  value={RichTextEditor.createValueFromString(
                    ref.content,
                    "html"
                  )}
                  readOnly
                />
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default FacultyReflections;
